import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, ClipboardText, Clock, CheckCircle, TrendUp, Trophy,
  Warning, Buildings, ArrowRight, Compass, Rocket
} from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { scoreBandClass } from "../lib/utils-extra";
import OnboardingChecklist from "../components/OnboardingChecklist";

function Stat({ label, value, icon: Icon, accent = "#0EA5E9", to }) {
  const card = (
    <Card className="surface-card border-[#E2E8F0]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="small-label">{label}</div>
            <div className="font-display text-3xl font-semibold mt-2 text-[#0F172A]">{value}</div>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
            <Icon size={22} weight="duotone" color={accent} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const isAdmin = user?.role === "owner" || user?.role === "hr";

  useEffect(() => {
    api.get("/analytics/dashboard").then(({ data }) => setData(data));
    if (isAdmin) {
      api.get("/onboarding/status").then(({ data }) => setOnboarding(data)).catch(() => {});
    }
  }, [isAdmin]);

  if (!data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
      </div>
    );
  }

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  // New / incomplete workspace → welcome + setup checklist instead of empty stats.
  if (isAdmin && onboarding && !onboarding.complete) {
    return (
      <div className="space-y-8" data-testid="dashboard-onboarding">
        <div className="rounded-3xl bg-[#0F172A] text-white p-8 sm:p-10 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[#0EA5E9] text-xs uppercase tracking-[0.18em] font-semibold">
              <Rocket size={16} weight="duotone" /> Welcome to RemediScore
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-3">
              Let's set up your workspace, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="text-[#CBD5E1] mt-2">
              Complete these {onboarding.total_count} quick steps to start running performance evaluations.
              You're <strong className="text-white">{onboarding.progress}%</strong> there.
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <div className="h-2 w-48 bg-white/15 rounded-full overflow-hidden">
                <div className="h-full bg-[#0EA5E9] rounded-full transition-all duration-500"
                     style={{ width: `${onboarding.progress}%` }} />
              </div>
              <span className="text-sm text-[#CBD5E1]">
                {onboarding.completed_count} of {onboarding.total_count} complete
              </span>
            </div>
          </div>
          <Compass size={220} weight="duotone" className="absolute -right-8 -bottom-12 text-white/5" />
        </div>

        <OnboardingChecklist status={onboarding} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="small-label">Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2 text-[#0F172A]">
            {greeting}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-[#475569] mt-1">Here's the pulse of your team today.</p>
        </div>
        {isAdmin && onboarding && (
          <Button
            variant="outline"
            onClick={() => setGuideOpen(true)}
            data-testid="open-setup-guide-button"
            className="shrink-0"
          >
            <Compass size={16} className="mr-2" /> Setup Guide
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Stat label="Total Employees" value={data.total_employees} icon={Users} accent="#0EA5E9" to="/employees" />
        <Stat label="Upcoming Reviews" value={data.upcoming_evaluations} icon={Clock} accent="#0284C7" to="/calendar" />
        <Stat label="Overdue" value={data.overdue_evaluations} icon={Warning} accent="#DC2626" to="/calendar" />
        <Stat label="Completed" value={data.completed_evaluations} icon={CheckCircle} accent="#16A34A" to="/evaluations" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="surface-card border-[#E2E8F0] lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="small-label">Average Score</div>
                <div className="font-display text-5xl font-semibold mt-2 text-[#0F172A]">
                  {data.average_score?.toFixed(1) || "0.0"}<span className="text-2xl text-[#475569]">%</span>
                </div>
                <p className="text-sm text-[#475569] mt-1">Across all finalized evaluations</p>
              </div>
              <TrendUp size={36} weight="duotone" color="#0EA5E9" />
            </div>

            <div className="mt-6">
              <div className="small-label mb-3">By Department</div>
              <div className="space-y-3">
                {data.department_averages?.length === 0 && (
                  <div className="text-sm text-[#475569]">No department data yet — finalize evaluations to see breakdown.</div>
                )}
                {data.department_averages?.map((d) => (
                  <div key={d.department} className="flex items-center gap-3">
                    <Buildings size={16} className="text-[#475569]" />
                    <div className="flex-1 text-sm font-medium">{d.department}</div>
                    <div className="flex-1">
                      <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0EA5E9] rounded-full" style={{ width: `${Math.min(100, d.avg)}%` }} />
                      </div>
                    </div>
                    <div className="font-mono-data text-sm font-semibold w-14 text-right">{d.avg.toFixed(1)}%</div>
                    <div className="text-xs text-[#475569] w-8 text-right">×{d.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <PerformerCard title="Top performer" data={data.top_performer} accent="#16A34A" icon={Trophy} />
          <PerformerCard title="Needs support" data={data.lowest_performer} accent="#D97706" icon={Warning} />
        </div>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display text-lg font-semibold">Run a new evaluation</div>
            <p className="text-sm text-[#475569]">Pick an employee and walk through the KPI checklist.</p>
          </div>
          <Link
            to="/evaluations/new"
            data-testid="new-evaluation-link"
            className="inline-flex items-center gap-2 bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            Start evaluation <ArrowRight size={16} />
          </Link>
        </CardContent>
      </Card>

      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="setup-guide-dialog">
          <DialogHeader>
            <DialogTitle>Setup Guide</DialogTitle>
            <DialogDescription>Your workspace setup checklist. Revisit any step anytime.</DialogDescription>
          </DialogHeader>
          <OnboardingChecklist status={onboarding} onNavigate={() => setGuideOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PerformerCard({ title, data, accent, icon: Icon }) {
  if (!data) {
    return (
      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <div className="small-label">{title}</div>
          <div className="text-sm text-[#475569] mt-3">No finalized evaluations yet.</div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className={`surface-card border-0 ${scoreBandClass(data.final_score || 0)}`}>
      <CardContent className="p-5 text-white">
        <div className="flex items-center gap-2 mb-3 opacity-90">
          <Icon size={16} weight="duotone" />
          <div className="text-xs uppercase tracking-[0.18em] font-semibold">{title}</div>
        </div>
        <div className="font-display text-xl font-semibold">{data.employee_name}</div>
        <div className="text-sm opacity-80 mt-0.5">{data.position_name} · {data.department}</div>
        <div className="font-display text-4xl font-semibold mt-4">{data.final_score?.toFixed(1)}<span className="text-lg opacity-80">%</span></div>
      </CardContent>
    </Card>
  );
}
